import * as React from 'react';
import { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Container,
  Flashbar,
  FlashbarProps,
  KeyValuePairs,
  SpaceBetween,
  Spinner,
  Tabs,
} from '@amzn/awsui-components-react-v3';
import { getSsoGroup, listSsoGroupResources } from 'src/api/omnismus';
import { DEFAULT_CONTENT_TYPE } from 'src/commons/constants';
import { CommonPageProps } from 'src/components/common/CommonPageProps';
import { AssociateAccountForm } from 'src/components/smus/associateAccountForm';
import { RegisterSsoGroupForm } from 'src/components/smus/registerSsoGroupForm';
import { AccountsTable } from 'src/components/smus/accountsTable';
import { ProjectsTable } from 'src/components/smus/projectsTable';
import { PageHeader } from '../common/PageHeader';
import {
  AccountEntry,
  extractGroupResources,
  GroupEntry,
  groupMetadataToGroupEntry,
  OmniSmusUnavailableHeader,
  ProjectEntry,
} from '../utils/smusUtil';
import { isOmniSmusAvailable } from 'src/api/config';

const RESOURCES_PAGE_SIZE = 50;

export interface SsoGroupDetailProps extends CommonPageProps {}

export const SsoGroupDetail = (props: SsoGroupDetailProps) => {
  const [groupEntry, setGroupEntry] = useState<GroupEntry | undefined>(undefined);
  const [accounts, setAccounts] = useState<AccountEntry[]>([]);
  const [projects, setProjects] = useState<ProjectEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [resourcesNextToken, setResourcesNextToken] = useState<string | undefined>(undefined);
  const [notifications, setNotifications] = useState<FlashbarProps.MessageDefinition[]>([]);
  const [accountFormModalVisible, setAccountFormModalVisible] = useState(false);
  const [selectedAccountEntry, setSelectedAccountEntry] = useState<AccountEntry | undefined>(undefined);
  const [groupFormModalVisible, setGroupFormModalVisible] = useState(false);

  useEffect(() => {
    props.setContentType(DEFAULT_CONTENT_TYPE);
    handleRefresh();
  }, []);

  const groupOptions = useMemo(
    () => (groupEntry ? [{ teamId: groupEntry.teamId, groupName: groupEntry.name }] : []),
    [groupEntry?.teamId, groupEntry?.name],
  );

  const groupMetadataId = decodeURIComponent(props.match.params.groupMetadataId);

  const handleRefresh = async () => {
    if (!isOmniSmusAvailable()) return;
    setLoading(true);
    setLoadFailed(false);
    setResourcesNextToken(undefined);
    setAccounts([]);
    setProjects([]);
    try {
      const [groupResponse, resourcesResponse] = await Promise.all([
        getSsoGroup({ groupMetadataId }),
        listSsoGroupResources({ groupMetadataId, maxResults: RESOURCES_PAGE_SIZE }),
      ]);
      const group = groupMetadataToGroupEntry(groupResponse.groupMetadata);
      setGroupEntry(group);
      const { newAccounts, newProjects } = extractGroupResources(group, resourcesResponse.resourceMetadataList ?? []);
      setAccounts(newAccounts);
      setProjects(newProjects);
      setResourcesNextToken(resourcesResponse.nextToken);
    } catch (err) {
      console.error('Failed to load SSO group', err);
      const message = err instanceof Error ? err.message : String(err);
      setLoadFailed(true);
      setNotifications([
        {
          type: 'error',
          content: `Failed to load SSO group: ${message}`,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
    setLoading(false);
  };

  const handleLoadMore = async () => {
    if (!resourcesNextToken || !groupEntry) return;
    setLoadingMore(true);
    try {
      const response = await listSsoGroupResources({
        groupMetadataId,
        nextToken: resourcesNextToken,
        maxResults: RESOURCES_PAGE_SIZE,
      });
      const { newAccounts, newProjects } = extractGroupResources(groupEntry, response.resourceMetadataList ?? []);
      setAccounts((prev) => [...prev, ...newAccounts]);
      setProjects((prev) => [...prev, ...newProjects]);
      setResourcesNextToken(response.nextToken);
    } catch (err) {
      console.error('Failed to load more resources', err);
      const message = err instanceof Error ? err.message : String(err);
      setNotifications([
        {
          type: 'error',
          content: `Failed to load more resources: ${message}`,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
    setLoadingMore(false);
  };

  if (loading) {
    return (
      <Box textAlign='center' padding={{ top: 'xxxl' }}>
        <Spinner size='large' />
      </Box>
    );
  }

  if (!groupEntry) {
    return (
      <>
        <Flashbar items={notifications} />
        <Box textAlign='center' padding={{ top: 'xxxl' }}>
          <Box variant='h3'>SSO group not found</Box>
        </Box>
      </>
    );
  }

  const buttonOptions = () => {
    const buttons = [];
    if (resourcesNextToken) {
      buttons.push({
        text: 'Load more resources',
        variant: 'normal',
        onItemClick: handleLoadMore,
        disabled: !resourcesNextToken || loading || loadingMore,
      });
    }
    buttons.push({
      text: '',
      icon: 'refresh',
      onItemClick: handleRefresh,
    });
    return buttons;
  };

  const detailsButtonOptions = () => {
    return [
      {
        text: '',
        icon: 'edit',
        onItemClick: () => setGroupFormModalVisible(true),
      },
    ];
  };

  if (!isOmniSmusAvailable()) {
    return <OmniSmusUnavailableHeader />;
  }

  return (
    <SpaceBetween size='l'>
      <Flashbar items={notifications} />
      <PageHeader buttons={buttonOptions()} header={groupEntry.name} variant='h1' />

      <Container header={<PageHeader buttons={detailsButtonOptions()} header={'Group details'} variant='h2' />}>
        <KeyValuePairs
          columns={2}
          items={[
            { label: 'Group ID', value: groupEntry.groupId },
            { label: 'Team ID', value: groupEntry.teamId },
            { label: 'Email', value: groupEntry.email },
            { label: 'Wiki', value: groupEntry.wiki },
            { label: 'Primary CTI', value: groupEntry.ctiString },
            { label: 'Resolver group', value: groupEntry.resolverGroup },
            { label: 'Use case', value: groupEntry.useCaseDescription },
            { label: 'Created by', value: groupEntry.createdBy },
            { label: 'Created at', value: groupEntry.createdAt },
          ]}
        />
      </Container>
      <Tabs
        tabs={[
          {
            label: `Associated accounts (${accounts.length})`,
            id: 'accounts',
            content: (
              <AccountsTable
                accounts={accounts}
                loading={loading}
                loadFailed={loadFailed}
                title='Accounts onboarded by this group'
                onAssociate={() => {
                  setSelectedAccountEntry(undefined);
                  setAccountFormModalVisible(true);
                }}
                onEdit={(account) => {
                  setSelectedAccountEntry(account);
                  setAccountFormModalVisible(true);
                }}
              />
            ),
          },
          {
            label: `SMUS projects (${projects.length})`,
            id: 'projects',
            content: (
              <ProjectsTable
                projects={projects}
                loading={loading}
                title="Projects hosted by this group's associated accounts"
              />
            ),
          },
        ]}
      />

      <AssociateAccountForm
        visible={accountFormModalVisible}
        onDismiss={() => setAccountFormModalVisible(false)}
        groups={groupOptions}
        editAccount={selectedAccountEntry}
        onSuccess={() => {
          setAccountFormModalVisible(false);
          setNotifications([
            {
              type: 'success',
              content: `Account ${selectedAccountEntry ? 'updated' : 'associated'} successfully.`,
              dismissible: true,
              onDismiss: () => setNotifications([]),
            },
          ]);
          handleRefresh();
        }}
        onError={(msg) => {
          setAccountFormModalVisible(false);
          setNotifications([
            {
              type: 'error',
              content: `Failed to ${selectedAccountEntry ? 'update' : 'associate'} account: ${msg}`,
              dismissible: true,
              onDismiss: () => setNotifications([]),
            },
          ]);
        }}
      />
      <RegisterSsoGroupForm
        visible={groupFormModalVisible}
        onDismiss={() => setGroupFormModalVisible(false)}
        editGroup={groupEntry}
        onSuccess={() => {
          setGroupFormModalVisible(false);
          setNotifications([
            {
              type: 'success',
              content: 'SSO group updated successfully.',
              dismissible: true,
              onDismiss: () => setNotifications([]),
            },
          ]);
          handleRefresh();
        }}
        onError={(msg) => {
          setGroupFormModalVisible(false);
          setNotifications([
            {
              type: 'error',
              content: `Failed to update SSO group: ${msg}`,
              dismissible: true,
              onDismiss: () => setNotifications([]),
            },
          ]);
        }}
      />
    </SpaceBetween>
  );
};
